import { useEffect, useRef, useState } from 'react'

// Reveals a long list in batches as a bottom sentinel scrolls into view, so we
// never render hundreds of cards at once. Attach `sentinelRef` to an element
// after the rendered slice; render only `items.slice(0, count)`. Pass a
// `resetKey` (e.g. the search query, or a collapsed/expanded flag) to snap the
// window back to the first batch when the underlying list changes.
export function useIncrementalReveal(
  total: number,
  opts: { batchSize?: number; resetKey?: string } = {},
) {
  const { batchSize = 8, resetKey = '' } = opts
  const [count, setCount] = useState(batchSize)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  // Snap back to the first batch when the underlying list changes, using the
  // "adjust state during render" pattern (no effect → no cascading render).
  const [prevKey, setPrevKey] = useState(resetKey)
  if (resetKey !== prevKey) {
    setPrevKey(resetKey)
    setCount(batchSize)
  }

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || count >= total) return
    const io = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) setCount(c => Math.min(c + batchSize, total))
      },
      { rootMargin: '400px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [count, total, batchSize])

  return { count: Math.min(count, total), sentinelRef }
}
